import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES } from "../data/content";

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.35 + i * 0.14 },
  }),
};

export default function Hero({ t, onEnquire }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <motion.img
          src={IMAGES.hero}
          alt="Bron Menai manor house, Newborough, Anglesey"
          className="w-full h-[115%] object-cover"
          initial={{ scale: 1.15, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 2, ease: [0.16, 1, 0.3, 1] }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-pine/80 via-pine/20 to-pine/10" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-[1600px] mx-auto w-full px-6 md:px-10 pb-14 md:pb-20 pt-40">
        <motion.p
          key={`eyebrow-${t.hero.eyebrow}`}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.25 }}
          className="font-mono text-[11px] md:text-xs uppercase tracking-[0.3em] text-gold mb-6"
          data-testid="hero-eyebrow"
        >
          {t.hero.eyebrow}
        </motion.p>

        <h1 className="font-serif font-light text-ivory text-4xl sm:text-6xl lg:text-7xl leading-[1.08] tracking-tight max-w-5xl" data-testid="hero-title">
          {t.hero.lines.map((line, i) => (
            <span key={line} className="hero-mask-line">
              <motion.span custom={i} variants={lineVariants} initial="hidden" animate="visible" className="block">
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-book-retreat-btn"
            onClick={() => onEnquire("retreat")}
            className="bg-terracotta text-ivory font-mono text-xs uppercase tracking-[0.2em] px-7 py-4 hover:bg-clay hover:scale-[1.02] transition-all duration-300"
          >
            {t.hero.bookRetreat}
          </button>
          <button
            data-testid="hero-book-wedding-btn"
            onClick={() => onEnquire("wedding")}
            className="border border-ivory/60 text-ivory font-mono text-xs uppercase tracking-[0.2em] px-7 py-4 hover:bg-ivory hover:text-pine transition-colors duration-300"
          >
            {t.hero.bookWedding}
          </button>
          <button
            data-testid="hero-enquire-stay-btn"
            onClick={() => onEnquire("stay")}
            className="text-ivory/80 font-mono text-xs uppercase tracking-[0.2em] px-4 py-4 underline underline-offset-8 decoration-ivory/40 hover:text-gold hover:decoration-gold transition-colors duration-300"
          >
            {t.hero.enquireStay}
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.3 }}
          className="mt-12 flex flex-wrap gap-x-8 gap-y-3 border-t border-ivory/20 pt-6"
          data-testid="hero-stats"
        >
          {t.hero.stats.map((stat) => (
            <span key={stat} className="font-mono text-[11px] uppercase tracking-[0.2em] text-ivory/70">
              {stat}
            </span>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
